import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import infoCircle from "../../assets/img/info-circle.svg";
import statusValid from "../../assets/img/status-valid.svg";
import statusError from "../../assets/img/status-error.svg";
import statusWarning from "../../assets/img/status-warning.svg";
import logo from "../../assets/img/dmarc-flow-logo.png";
import ScoreChart from "./ScoreChart";
import './AnalyzeResults.css';
import {useTranslation} from "react-i18next";
import BimiResult from "./BimiResult";
import hostname from "../../hostname";

const AnalyzeResults = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Registration form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Function to toggle the modal
    const handleShow = () => {
        setShowModal(true);
        setError('');
        setSuccess(false);
    };
    const handleClose = () => {
        setShowModal(false);
        setName('');
        setEmail('');
        setPassword('');
        setError('');
        setSuccess(false);
    };

    // Registration API call
    const handleRegister = async () => {
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            const response = await fetch(hostname + '/index.php/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email,
                    password,
                }),
            });

            if (!response.ok) {
                throw new Error(`Registration failed: ${response.statusText}`);
            }

            setSuccess(true);
            setTimeout(() => {
                handleClose(); // Close modal after success
            }, 2000);

        } catch (err) {
            setError(err.message || 'An error occurred during registration.');
        } finally {
            setLoading(false);
        }
    };

    // Retrieve the validation data from the Redux store.
    const validation = useSelector((state) => state.validation);
    const dmarc = validation?.payload?.dmarc || {};
    const spf = validation?.payload?.spf || {};
    const resultDomain = validation?.payload?.domain || {};
    const bimi = validation?.payload?.bimi || {};
    const date = validation?.payload?.date || {};
    const dkim = validation?.payload?.dkim || {};
    const riskAssessment = validation?.payload?.riskAssesment || {};
    const score = validation?.payload?.score || 0;

    // useEffect: Check if a domain parameter is provided and, if so, fetch from the API.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const domain = params.get('domain');
        if (domain) {

            fetch(hostname + `/index.php/domain/analyze/${domain}`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then(json => {
                    // Dispatch an action to update the validation data in the store.
                    dispatch({type: 'SET_VALIDATION_RESULT', payload: json});
                })
                .catch(err => {
                    console.error('Fetch error:', err);
                });
        }
    }, [dispatch]);

    // Until we have data from the store, show a loading message.
    if (!validation?.payload) {
        return <div>Loading...</div>;
    }

    // Check the age of the data and navigate away if it’s older than 10 minutes.
    const storedTime = new Date(date.date).getTime();
    const currentTime = new Date().getTime();
    const differenceInMinutes = (currentTime - storedTime) / 60000; // Convert ms to minutes

    const params = new URLSearchParams(window.location.search);
    const domain = params.get('domain');

    if (domain === null && differenceInMinutes > 10) {
        // navigate("/");
        // return null;
    }

    const riskColorMapping = {
        'NOT_SUBSCRIBED': 'secondary',
        'VERY_LOW': 'success',
        'LOW': 'success',
        'MODERATE': 'warning',
        'HIGH': 'danger',
        'CRITICAL': 'danger'
    };
    const borderColor = riskColorMapping[riskAssessment.key] || 'primary';

    const renderTooltip = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            {t('pages.analyzeResults.tooltip.description')}
        </Tooltip>
    );

    const getBorderClass = (protocol) => {
        for (const p of protocol) {
            if (p.issues.length > 0) return "border border-danger-subtle";
            if (p.warnings.length > 0) return "border border-warning-subtle";
        }
        return "border border-success-subtle";
    };
    const getPicture = (protocol) => {
        for (const p of protocol) {
            if (p.issues.length > 0) return statusError;
            if (p.warnings.length > 0) return statusWarning;
        }
        return statusValid;
    };

    const getDmarcTooltip = (dmarc) => {
        const warnings = {
            DMARC_WARN_INVALID_SEGMENT: t('pages.analyzeResults.dmarc.invalidSegment'),
            DMARC_WARN_DUPLICATE_TAG: t('pages.analyzeResults.dmarc.duplicateTag'),
            DMARC_WARN_P_NOT_REJECT: t('pages.analyzeResults.dmarc.noReject'),
            DMARC_WARN_RUA_NO_MAILTO: t('pages.analyzeResults.dmarc.ruaNoMailto'),
            DMARC_WARN_INVALID_RUA: t('pages.analyzeResults.dmarc.invalidRua'),
            DMARC_WARN_RUF_NO_MAILTO: t('pages.analyzeResults.dmarc.rufNoMailto'),
            DMARC_WARN_INVALID_RUF: t('pages.analyzeResults.dmarc.invalidRuf'),
            DMARC_WARN_SP_NOT_REJECT: t('pages.analyzeResults.dmarc.spNotReject'),
            DMARC_WARN_INVALID_PCT: t('pages.analyzeResults.dmarc.invalidPct'),
        };

        const issues = {
            INVALID_DMARC_VERSION: t('pages.analyzeResults.dmarc.invalidDmarcVersion'),
            DMARC_ERR_INVALID_P: t('pages.analyzeResults.dmarc.dmarcErrInvalidP'),
            DMARC_ERR_MISSING_SP: t('pages.analyzeResults.dmarc.dmarcErrMissingSp'),
            UNKNOWN_ENTRY: t('pages.analyzeResults.dmarc.unknownEntry'),
            MISSING_DMARC_VERSION_DEFINITION: t('pages.analyzeResults.dmarc.missingDmarcVersionDefinition'),
            MISSING_DMARC_POLICY_DEFINITION: t('pages.analyzeResults.dmarc.missingDmarcPolicyDefinition')
        };
        let issueString = '';
        let warningString = '';

        for (const d of dmarc) {
            for (const status of d.warnings) {
                if (warnings[status.key]) {
                    warningString = warningString + '<strong>' + warnings[status.key] + '</strong></br></br>'
                }
            }
            for (const status of d.issues) {
                if (issues[status.key]) {
                    issueString = issues + '<strong>' + issueString[status.key] + '</strong></br></br>'
                }
            }
        }


        if (issueString.length === 0 && warningString.length === 0) {
            return (<Tooltip></Tooltip>);
        }

        let className = "bg-warning text-dark";
        let toolTipClassName = "warning";
        if (warningString.length > 0) {
            className = "bg-danger text-white";
            toolTipClassName = "danger";
        }
        return (
            <Tooltip id={`tooltip-` + toolTipClassName} className={className}>
                <div dangerouslySetInnerHTML={{__html: warningString + issueString}}/>
            </Tooltip>
        );
    };
    const getSpfTooltip = (spf) => {
        const warnings = {
            SOFT_REJECT_ALLOWS_POTENTIAL_MALICIOUS_SENDER: t('pages.analyzeResults.spf.softRejectAllowsPotentialMaliciousSender'),
            NEUTRAL_SPF_RECORD_NOT_FORCING: t('pages.analyzeResults.spf.neutralSpfRecordNotForcing'),
            UNKNOWN_MECHANISM: t('pages.analyzeResults.spf.unknownMechanism'),
            MISSING_MINUS_ALL_POTENTIAL_UNAUTHORIZED_SENDERS_ALLOWED: t('pages.analyzeResults.spf.missingMinusAllPotentialUnauthorizedSendersAllowed')
        };
        const issues = {
            INVALID_IP_ADDRESS: t('pages.analyzeResults.spf.invalidIpAddress'),
            MISSING_DOMAIN_IN_INCLUDE: t('pages.analyzeResults.spf.missingDomainInInclude')
        };
        let issueString = '';
        let warningString = '';

        for (const d of spf) {
            for (const status of d.warnings) {
                if (warnings[status.key]) {
                    warningString = warningString + '<strong>' + warnings[status.key] + '</strong></br></br>'
                }
            }
            for (const status of d.issues) {
                if (issues[status.key]) {
                    issueString = issues + '<strong>' + issueString[status.key] + '</strong></br></br>'
                }
            }
        }


        if (issueString.length === 0 && warningString.length === 0) {
            return (<Tooltip></Tooltip>);
        }

        let className = "bg-warning text-dark";
        let toolTipClassName = "warning";
        if (warningString.length > 0) {
            className = "bg-danger text-white";
            toolTipClassName = "danger";
        }
        return (
            <Tooltip id={`tooltip-` + toolTipClassName} className={className}>
                <div dangerouslySetInnerHTML={{__html: warningString + issueString}}/>
            </Tooltip>
        );
    };

    const getDkimTooltip = (dkim) => {
        const warnings = {
            NOT_STRONGEST_ALGORITHM: t('pages.analyzeResults.dkim.notStrongestAlgorithm'),
            DEFAULT_ENCRYPTION_METHOD: t('pages.analyzeResults.dkim.defaultEncryptionMethod'),
            NOT_RECOMMENDED_FLAG: t('pages.analyzeResults.dkim.notRecommendedFlag'),
            UNKNOWN_MECHANISM: t('pages.analyzeResults.dkim.unknownMechanism'),
            MISSING_FLAG: t('pages.analyzeResults.dkim.missingFlag')
        };

        const issues = {
            INVALID_DKIM_VERSION: t('pages.analyzeResults.dkim.invalidDkimVersion'),
            WEAK_ENCRYPTION_ALGORITHM: t('pages.analyzeResults.dkim.weakEncryptionAlgorithm'),
            EMPTY_PUBLIC_KEY: t('pages.analyzeResults.dkim.emptyPublicKey'),
            MISSING_VERSION_INFO: t('pages.analyzeResults.dkim.missingVersionInfo'),
            MISSING_PUBLIC_KEY: t('pages.analyzeResults.dkim.missingPublicKey')
        };

        let issueString = '';
        let warningString = '';

        for (const d of dkim) {
            for (const status of d.warnings) {
                if (warnings[status.key]) {
                    warningString = warningString + '<strong>' + warnings[status.key] + '</strong></br></br>'
                }
            }
            for (const status of d.issues) {
                if (issues[status.key]) {
                    issueString = issues + '<strong>' + issueString[status.key] + '</strong></br></br>'
                }
            }
        }


        if (issueString.length === 0 && warningString.length === 0) {
            return (<Tooltip></Tooltip>);
        }

        let className = "bg-warning text-dark";
        let toolTipClassName = "warning";
        if (warningString.length > 0) {
            className = "bg-danger text-white";
            toolTipClassName = "danger";
        }
        return (
            <Tooltip id={`tooltip-` + toolTipClassName} className={className}>
                <div dangerouslySetInnerHTML={{__html: warningString + issueString}}/>
            </Tooltip>
        );
    };

    return (
        <Container className="mt-4">
            <Row className={"mb-3"}>
                <Col md={10}>
                    <h2 className={"fw-light"}>{t('pages.analyzeResults.scanningResults')}<strong
                        className={"fw-bold"}> {resultDomain}</strong>
                    </h2>
                </Col>
                <Col md={2} className={"d-flex justify-content-end"}>
                    <Button className={"btn btn-light btn-outline-primary text-primary"} onClick={handleShow}>
                        {t('pages.analyzeResults.getFullReport')}
                    </Button>
                </Col>
            </Row>

            <Row className="justify-content-center">
                <Col>
                    <Card border={borderColor} className="mb-3">
                        <Card.Body>
                            {t('pages.analyzeResults.riskAssessmentLevel')} {riskAssessment.value}
                            <Card.Text>
                                <strong>{t('pages.analyzeResults.overallSecurity')}
                                    {t('pages.analyzeResults.score')}</strong> {score} / {score === 10 ? 10 : 10}
                            </Card.Text>
                            <Card.Text>
                                {riskAssessment.description}
                            </Card.Text>
                            <Button variant="primary" className={"text-white"} onClick={handleShow}>
                                {t('pages.analyzeResults.subscribeNow')}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="justify-content-center">
                <Col>
                    <Card border={"primary"} className="mb-3">
                        <Card.Body>
                            <Container>
                                <Row>
                                    <Col>
                                        {t('pages.analyzeResults.overallResult')}
                                        <OverlayTrigger
                                            placement="right"
                                            delay={{show: 250, hide: 400}}
                                            overlay={renderTooltip}
                                        >
                                            <img
                                                alt={t('pages.analyzeResults.tooltip.hint')}
                                                src={infoCircle}
                                                width="30"
                                                height="15"
                                            />
                                        </OverlayTrigger>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <ScoreChart score={score} maxScore={10}/>
                                    </Col>
                                    <Col>
                                        <OverlayTrigger trigger={["hover", "focus"]} placement="top"
                                                        overlay={getDmarcTooltip(dmarc)}>
                                            <Container
                                                className={`${getBorderClass(dmarc)} border rounded justify-content-center d-flex flex-column align-items-center pt-3 pb-3`}>
                                                <h4>{t('pages.analyzeResults.dmarc.title')}</h4>
                                                <img
                                                    alt={t('pages.analyzeResults.dmarc.hint')}
                                                    src={getPicture(dmarc)}
                                                    width="90"
                                                    height="60"
                                                />
                                                <p className={"small text-center"}>{t('pages.analyzeResults.dmarc.hint')}</p>
                                            </Container>
                                        </OverlayTrigger>
                                    </Col>
                                    <Col>
                                        <OverlayTrigger trigger={["hover", "focus"]} placement="top"
                                                        overlay={getSpfTooltip(spf)}>
                                            <Container
                                                className={`${getBorderClass(spf)} border rounded justify-content-center d-flex flex-column align-items-center pt-3 pb-3`}>
                                                <h4>{t('pages.analyzeResults.spf.title')}</h4>
                                                <img
                                                    alt={t('pages.analyzeResults.spf.hint')}
                                                    src={getPicture(spf)}
                                                    width="90"
                                                    height="60"
                                                />
                                                <p className={"small text-center"}>{t('pages.analyzeResults.spf.hint')}</p>
                                            </Container>
                                        </OverlayTrigger>
                                    </Col>
                                    <Col>
                                        <OverlayTrigger trigger={["hover", "focus"]} placement="top"
                                                        overlay={getDkimTooltip(dkim)}>
                                            <Container
                                                className={`${getBorderClass(dkim)} border rounded justify-content-center d-flex flex-column align-items-center pt-3 pb-3`}>
                                                <h4>{t('pages.analyzeResults.dkim.title')}</h4>
                                                <img
                                                    alt={t('pages.analyzeResults.dkim.hint')}
                                                    src={getPicture(dkim)}
                                                    width="90"
                                                    height="60"
                                                />
                                                <p className={"small text-center"}>{t('pages.analyzeResults.dkim.hint')}</p>
                                            </Container>
                                        </OverlayTrigger>
                                    </Col>
                                </Row>
                                <BimiResult bimiData={bimi}></BimiResult>
                                <Row className={"text-center mt-5 mb-5"}>
                                    <Col>
                                        <Container className={"mt-5"}>
                                            <p className="fs-4">
                                                {t('pages.analyzeResults.whatsNext.description')}
                                            </p>
                                        </Container>
                                        <Button variant="primary" className={"text-white"} onClick={handleShow}>
                                            {t('pages.analyzeResults.whatsNext.buttonText')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Container>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Registration Modal */}
            <Modal show={showModal} onHide={handleClose} centered backdrop="blur" className="blurred-background">
                <Modal.Header closeButton className={"border-0"}>
                    <img src={logo} alt="Logo" style={{height: "40px", marginRight: "10px"}}/>
                    <Modal.Title>{t('pages.analyzeResults.modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">Registration successful! Redirecting...</Alert>}

                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('pages.analyzeResults.modal.form.username')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t('pages.analyzeResults.modal.form.username')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('pages.analyzeResults.modal.form.email')}</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder={t('pages.analyzeResults.modal.form.username')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('pages.analyzeResults.modal.form.password')}</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder={t('pages.analyzeResults.modal.form.password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className={"border-0"}>
                    {/*<Button variant="secondary" onClick={handleClose} disabled={loading}>Close</Button>*/}
                    <Button variant="primary" onClick={handleRegister} disabled={loading} className={"text-white"}>
                        {loading ? <Spinner as="span" animation="border" size="sm" role="status"
                                            aria-hidden="true"/> : t('pages.analyzeResults.modal.form.register')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AnalyzeResults;
